import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDTreeSelect from '../../../../../../../../../../../BaseComponents/TreeSelect/EDTreeSelect';
import {
  ISelection,
  IBaseSelection,
} from '../../../../../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import {
  IFavorites,
  EKAnalysGroupingModeType,
} from '../EkonomiskAnalysFavoriteAnalysDialog';
import {
  isFavorite,
  analyserToTreeSections,
  isStartAnalys,
} from '../Utils/EkonomiskAnalysFavoriteAnalysDialogUtils';
import EDTypography from '../../../../../../../../../../../BaseComponents/Typography/EDTypography';
import mergeSort, {
  stringCompareNumeric,
} from '../../../../../../../../../../../Utils/mergeSort';
import EDIconButton from '../../../../../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDIcon from '../../../../../../../../../../../BaseComponents/Icon/EDIcon';
import { IStartAnalysInfo } from '../Queries/ChangeStartAnalysMutation/ChangeStartAnalysMutation';
import DeleteAnalysMutation from '../../../../Components/SaveAnalys/Mutations/DeleteAnalysMutation/DeleteAnalysMutation';
import { ActiveDatabaseConsumer } from '../../../../../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { EkonomiskAnalysSelectConsumer } from '../../../../Components/AnalysSelect/Context/EkonomiskAnalysSelectContext';
import EkonomiskAnalysDeleteAnalysConfirmation from '../../../../Components/SaveAnalys/Components/EkonomiskAnalysDeleteAnalysConfirmation/EkonomiskAnalysDeleteAnalysConfirmation';
import EDTooltip from '../../../../../../../../../../../BaseComponents/Tooltip/EDTooltip';
import {
  ICheckedItems,
  ITreeSection,
  searchValueFilter,
  updateChecked,
} from '../../../../../../../../../../../BaseComponents/TreeSelect/TreeSelectUtils';

export interface IAnalysData {
  kodBehName: string;
  kodBehId: string;
  kodBehKombinerad: boolean;
  idKod?: string;
  utdelatLopnr?: number | null;
  kategori?: string;
  baseSelection?: IBaseSelection;
  // id: string; // Analysens id
  // idkod?: string; // Analysens idkod om standard rapport
  // name: string; // Analysens namn
  // favoritLopnr: number | null;
  // utdelatLopnr: number | null;
  // kategori: string;
  // defaultFromYear?: number;
  // defaultFromPeriod?: number;
  // obligatorisk: boolean;
  // obligatoriskRoller: IGBSRole[];
}

interface IProps {
  analyser: ISelection[];
  favorites: IFavorites;
  onAnalysClicked: (
    sections: ITreeSection<IAnalysData>[],
    checked: boolean
  ) => void;
  updateFavorites: (favorites: IFavorites) => void;
  sortMode: EKAnalysGroupingModeType;
  categories: string[];
  startAnalys?: IStartAnalysInfo;
  updateStartAnalys: (analysInfo?: IStartAnalysInfo) => void;
  searchValue: string;
  classes?: any;
}

interface IState {
  deleteConfirmationId: string | null;
  deleteConfirmationName: string | null;
  sections: ITreeSection<IAnalysData>[];
}

const styles = (
  theme: Theme,
  _params: any,
  classes: Record<'analysItemContainer', string>
) => ({
  container: {
    // FIXME: @Marcus, '!important' har inte i stilarna att göra, varför är de nödvändiga?
    paddingRight: `${theme.spacing(4)} !important`,
    paddingLeft: `${theme.spacing(4)} !important`,
    paddingBottom: `${theme.spacing(5)} !important`,
  },
  sectionHeaderContainer: {
    display: 'flex',
    flex: '0 1 auto',
    alignItems: 'center',
  },
  analysItemContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(3),
  },
  secondaryLabel: {
    fontSize: '0.775rem',
  },
  secondaryText: {
    fontSize: '0.775rem',
  },
});

type SetDeleteConfirmationDetailsFnType = (
  id: string | null,
  name: string | null
) => void;

const sortAnalyser = (props: IProps): ITreeSection<IAnalysData>[] => {
  return mergeSort(
    analyserToTreeSections(props.analyser, props.sortMode, props.favorites),
    (a, b) => {
      if (props.sortMode === 'kodbeh') {
        if (a.data?.kodBehKombinerad && !b.data?.kodBehKombinerad) {
          return -1;
        } else if (b.data?.kodBehKombinerad && !a.data?.kodBehKombinerad) {
          return 1;
        } else {
          return stringCompareNumeric(a.name, b.name);
        }
      }
      return stringCompareNumeric(a.name, b.name);
    }
  );
};

class EkonomiskAnalysFavoriteAnalysList extends React.PureComponent<
  IProps,
  IState
> {
  constructor(props: IProps) {
    super(props);

    const sections: ITreeSection<IAnalysData>[] = sortAnalyser(props);

    this.state = {
      deleteConfirmationId: null,
      deleteConfirmationName: null,
      sections: sections,
    };
  }

  componentDidUpdate(prevProps: IProps) {
    if (
      prevProps.sortMode !== this.props.sortMode ||
      prevProps.analyser !== this.props.analyser
    ) {
      this.setState({
        sections: searchValueFilter(
          sortAnalyser(this.props),
          this.props.searchValue,
          true
        ).sections,
      });
    }
  }

  updateSections = (sections: ITreeSection<IAnalysData>[]) => {
    this.setState({
      sections: sections,
    });
  };

  setDeleteConfirmationDetails: SetDeleteConfirmationDetailsFnType = (
    id: string | null,
    name: string | null
  ) => {
    this.setState({ deleteConfirmationId: id, deleteConfirmationName: name });
  };

  onCheck = (
    sections: ITreeSection<IAnalysData>[],
    checkedItems: ICheckedItems,
    checked: boolean,
    suggestion?: ITreeSection<IAnalysData>
  ) => {
    const allItems = [
      suggestion ?? checkedItems.checkedItem,
      ...checkedItems.children,
    ];
    this.props.onAnalysClicked(allItems, checked);
    const newSections = updateChecked(
      checkedItems.checkedItem,
      sections,
      checked,
      suggestion
    ).updatedSections;
    this.updateSections(newSections);
    if (!checked) {
      const startAnalys = allItems.find(this.isStartAnalys);
      if (startAnalys) {
        this.updateStartAnalys(null, startAnalys);
      }
    }

    return sections;
  };

  updateStartAnalys = (
    event: React.MouseEvent<HTMLElement> | null,
    analys: ITreeSection<IAnalysData>
  ) => {
    if (event) {
      event.stopPropagation();
    }

    if (
      analys.data?.kodBehId &&
      !isFavorite(
        analys.data.kodBehId,
        analys.value,
        analys.data?.utdelatLopnr ?? null,
        this.props.favorites
      )
    ) {
      this.onCheck(
        this.state.sections,
        { checkedItem: analys, children: [] },
        true
      );
    }

    const selection = this.props.analyser.find(
      x => x.id === analys.data?.kodBehId
    );

    if (selection) {
      const newAnalys = selection.analysList.find(
        x =>
          x.id === analys.value && x.utdelatLopnr === analys.data?.utdelatLopnr
      );
      if (newAnalys && newAnalys.utdelatLopnr !== null) {
        const isSelected =
          this.props.startAnalys &&
          newAnalys &&
          isStartAnalys(analys, this.props.startAnalys);
        if (analys.data?.kodBehId) {
          this.props.updateStartAnalys(
            isSelected
              ? undefined
              : {
                  kodbehorighet: analys.data.kodBehId,
                  idKod: newAnalys.idkod,
                  utdelningLopnr: newAnalys.utdelatLopnr ?? null,
                  rapportId: newAnalys.idkod ? undefined : newAnalys.id,
                  kodplanId: undefined,
                }
          );
        }
      }
    }
  };

  isStartAnalys = (analys: ITreeSection<IAnalysData>): boolean => {
    if (this.props.startAnalys) {
      return isStartAnalys(analys, this.props.startAnalys);
    }
    return false;
  };

  renderListItemButtons = (suggestion: ITreeSection<IAnalysData>) => {
    const hasChildren = suggestion.subList && suggestion.subList.length > 0;
    const startAnalys = !hasChildren ? this.isStartAnalys(suggestion) : false;
    return (
      !hasChildren && (
        <>
          {(suggestion.data?.kategori?.toLowerCase() ?? '') ===
            'egna lokala' && (
            <EDIconButton
              tooltip="Ta bort"
              icon={<EDIcon color="inherit" iconType="delete" />}
              onClick={() =>
                this.setDeleteConfirmationDetails(
                  suggestion.value,
                  suggestion.name
                )
              }
              margin="none"
            />
          )}
          <EDIconButton
            icon={
              <EDIcon
                color="inherit"
                iconType={startAnalys ? 'star' : 'star_border'}
              />
            }
            onClick={event => this.updateStartAnalys(event, suggestion)}
            tooltip={
              startAnalys ? 'Ta bort som startanalys' : 'Välj som startanalys'
            }
            margin="none"
          />
        </>
      )
    );
  };

  hoverChecklistButtons = (section: ITreeSection<IAnalysData>) => {
    return !this.isStartAnalys(section);
  };

  renderSecondaryText = (suggestion: ITreeSection<IAnalysData>) => {
    return (
      <>
        <EDTooltip
          title={
            this.props.sortMode === 'kodbeh'
              ? 'Kodbehörighet'
              : 'Rapportkategori'
          }
        >
          <EDTypography
            color="textSecondary"
            className={this.props.classes.secondaryText}
          >
            {this.props.sortMode === 'kodbeh'
              ? suggestion.data?.kategori
              : suggestion.data?.kodBehName}
          </EDTypography>
        </EDTooltip>
        {suggestion.data?.baseSelection &&
          suggestion.data.baseSelection.name && (
            <EDTooltip title="Grundurval">
              <EDTypography
                color="textSecondary"
                className={this.props.classes.secondaryText}
              >
                {suggestion.data.baseSelection.name}
              </EDTypography>
            </EDTooltip>
          )}
      </>
    );
  };

  render() {
    return (
      <ActiveDatabaseConsumer>
        {({ activeDatabase }) => (
          <EkonomiskAnalysSelectConsumer>
            {({ selectedAnalys }) => (
              <DeleteAnalysMutation
                currentStartAnalys={this.props.startAnalys}
                deselectCurrentAnalys={false}
                selectedAnalys={selectedAnalys}
              >
                {deleteAnalys => (
                  <>
                    <EDTreeSelect
                      searchValue={this.props.searchValue}
                      sections={this.state.sections}
                      sectionsUpdate={this.updateSections}
                      renderListItemButtons={this.renderListItemButtons}
                      renderSecondaryText={this.renderSecondaryText}
                      hoverChecklistButtons={this.hoverChecklistButtons}
                      onCheck={this.onCheck}
                      isInitialStateOpen
                    />
                    {this.state.deleteConfirmationId && (
                      <EkonomiskAnalysDeleteAnalysConfirmation
                        benamning={
                          this.state.deleteConfirmationName ||
                          'Analysnamn saknas'
                        }
                        open={
                          this.state.deleteConfirmationId !== null &&
                          this.state.deleteConfirmationId !== ''
                        }
                        toggleOpen={() =>
                          this.setDeleteConfirmationDetails(null, null)
                        }
                        deleteAnalys={() => {
                          deleteAnalys(
                            { id: this.state.deleteConfirmationId || '' },
                            activeDatabase || ''
                          )();
                          this.setDeleteConfirmationDetails(null, null);
                        }}
                        lokalGemensam={false}
                      />
                    )}
                  </>
                )}
              </DeleteAnalysMutation>
            )}
          </EkonomiskAnalysSelectConsumer>
        )}
      </ActiveDatabaseConsumer>
    );
  }
}

export default withStyles(EkonomiskAnalysFavoriteAnalysList, styles, {
  uniqId: '4f947329-a5eb-416d-98de-0f563d38a8be',
});
